import React from 'react';
import {List, Set} from 'immutable';
import connectToStores from 'alt-utils/lib/connectToStores';
import KpiGoalActions from '@store/kpi/KpiTargetActions';
import MetricsActions from '@store/metrics-trends/NineBoxMetricActions';
import MetricsTrendsActions from '@store/metrics-trends/NineBoxMetricActions';
import SkillActions from 'actions/SkillActions';
import DirectReportsActions from 'actions/coaching/DirectReportsActions';
import TeamListStore from 'stores/TeamListStore';
import DirectReportsStore from 'stores/coaching/DirectReportsStore';
import {Moment} from 'moment';
import User, {UserId} from 'records/User';
import {MetricAxis} from '@store/@types/metric';
import fetchMissingUsers from 'utils/fetchMissingUsers';
import MetricsTrendsStore, {
  DEFAULT_HORIZONTAL_METRIC,
  DEFAULT_VERTICAL_METRIC,
} from '@store/metrics-trends/NineBoxMetricStore';
import SkillStore from 'stores/SkillStore';
import UserStore from 'stores/UserStore';
import CoachingPlanStore from 'stores/coaching/CoachingPlanStore';
import isEqual from 'lodash/isEqual';
import TeamActions from 'actions/TeamActions';
import {NineBox} from 'components/dashboard/manager/nine-box/NineBox';
import uniq from 'lodash/uniq';
import {
  getMetricFilter,
  getTeams,
  titleByMetric,
  xAxisOptions,
  yAxisOptions,
} from 'components/dashboard/manager/nine-box/NineBox.utils';
import RepCoachingActions from '@store/coaching-plans/rep/RepCoachingActions';
import RepCoachingStore from '@store/coaching-plans/rep/RepCoachingStore';
import * as queryParams from 'utils/queryParams';
import {getStringQueryParam} from 'utils/getQueryParam';
import KpiTargetActions from '@store/kpi/KpiTargetActions';
import {getChartData} from 'components/dashboard/manager/nine-box/MultiRepData.utils';
import CoachingPlan from 'records/CoachingPlan';
import KpiTargetStore from '@store/kpi/KpiTargetStore';
import {TeamId} from 'records/TeamId';

interface NineBoxContainerProps {
  startDate: Moment;
  endDate: Moment;
  selectedTeamId: TeamId;
  adminId?: UserId;
  managerId: UserId;
  onCreatePlan: (rep: User) => void;
  managerToDisplay: User;
  ownSummary: boolean;
  location: any;
}

interface NineBoxContainerState {
  initialLoad: boolean;
  repDataLoading: boolean;
}

function getPropsFromStores({
  managerId,
  selectedTeamId,
  startDate,
  endDate,
}: NineBoxContainerProps) {
  const kpisById = KpiTargetStore.getState().kpisById;
  const metricData = MetricsTrendsStore.getState();
  const usedSkills = SkillStore.getState().usedSkills;
  const allTeams = TeamListStore.getState().teams;
  const users = UserStore.getState().users;
  const planMap = CoachingPlanStore.getState().planMap;
  const {teams: managerTeams, skillBreakdowns} = DirectReportsStore.getState();
  const {plansByRep, timeSpentByPlan, velocityMap} = RepCoachingStore.getState();

  // Used to get labels for the metric being measured (kpi, skill, default)
  const titleMap = titleByMetric(usedSkills, kpisById);
  const chartData = getChartData(
    metricData,
    users,
    kpisById,
    skillBreakdowns,
    planMap,
    titleMap,
    managerId
  );

  const teams = getTeams(managerId, selectedTeamId, allTeams.toList(), managerTeams);
  const metricFilter = getMetricFilter(startDate, endDate, teams);
  const coachingRepIds: Set<UserId> = managerId
    ? planMap
        .toList()
        .filter(plan => plan.coach.id === managerId && !plan.completed)
        .map(plan => plan.recipient.id)
        .toSet()
    : Set();
  const horizontalMetrics = xAxisOptions(usedSkills, kpisById);
  const verticalMetrics = yAxisOptions(kpisById);

  return {
    teams: Set(teams),
    users,
    metricFilter,
    kpisById,
    metricData,
    chartData,
    coachingRepIds,
    xAxisOptions: horizontalMetrics,
    yAxisOptions: verticalMetrics,
    titleMap,
    plansByRep,
    timeSpentByPlan,
    velocityMap,
  };
}

@connectToStores
export default class NineBoxContainer extends React.PureComponent<
  NineBoxContainerProps & ReturnType<typeof getPropsFromStores>,
  NineBoxContainerState
> {
  state: NineBoxContainerState = {
    initialLoad: true,
    repDataLoading: false,
  };

  static getStores() {
    return [
      KpiTargetStore,
      MetricsTrendsStore,
      SkillStore,
      TeamListStore,
      UserStore,
      CoachingPlanStore,
      DirectReportsStore,
      RepCoachingStore,
    ];
  }

  static getPropsFromStores(props) {
    return getPropsFromStores(props);
  }

  componentDidMount() {
    this.initialDataFetch();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.initialLoad) {
      return;
    }
    const {chartData} = this.props;

    this.metricUpdate(prevProps);
    this.repUpdate(prevProps);

    const repIds = Set(chartData.map(d => d.rep.id));
    const oldRepIds = Set(chartData.map(d => d.rep.id));
    if (!repIds.equals(oldRepIds)) {
      DirectReportsActions.getSkillBreakdown(repIds.toJS()).catch(console.error);
      fetchMissingUsers(repIds.toList());
    }
  }

  getSelectedRep(props: NineBoxContainerProps = this.props) {
    return getStringQueryParam(props.location, 'selectedRep');
  }

  async repDataLoad(location, startDate, endDate) {
    const selectedRep = this.getSelectedRep();
    if (!selectedRep) return;

    try {
      this.setState({repDataLoading: true});
      const plans = await RepCoachingActions.fetchRepPlans({
        repId: selectedRep,
        startDate,
        endDate,
      });
      const planIds = plans.map(p => p.id);

      await Promise.all([
        RepCoachingActions.fetchTimeSpent(planIds.toJS()),
        RepCoachingActions.fetchVelocity(planIds.toJS()),
        KpiTargetActions.fetchUsedByDateAndTeams(startDate, endDate, selectedRep),
        SkillActions.fetchUsedByDate(startDate, endDate, selectedRep),
      ]);
    } catch (e) {
      console.error(e);
    } finally {
      this.setState({repDataLoading: false});
    }
  }

  /**
   * Updates all data which is rep specific
   */
  repUpdate(prevProps: NineBoxContainerProps) {
    const {startDate, endDate, location} = this.props;
    const selectedRep = this.getSelectedRep();
    const prevRep = this.getSelectedRep(prevProps);

    if (!selectedRep) {
      return;
    }

    const sameDates = startDate.isSame(prevProps.startDate) && endDate.isSame(prevProps.endDate);
    const sameRep = prevRep === selectedRep;
    if (sameDates && sameRep) {
      return;
    }

    this.repDataLoad(location, startDate, endDate).catch(console.error);
  }

  metricUpdate(prevProps) {
    const {
      metricFilter,
      metricData: {horizontal, vertical},
      startDate,
      endDate,
      teams,
    } = this.props;
    if (!metricFilter) {
      return;
    }

    // Fetch new metric data on these changes
    const filterChanged = !isEqual(metricFilter, prevProps.metricFilter);
    const fetchHorizontal =
      filterChanged || !horizontal.metric.equals(prevProps.metricData.horizontal.metric);
    const fetchVertical =
      filterChanged || !vertical.metric.equals(prevProps.metricData.vertical.metric);
    const repDropped = !!this.getSelectedRep(prevProps) && !this.getSelectedRep();

    if (filterChanged) {
      setTimeout(() => {
        MetricsActions.fetchTimeInformation(metricFilter);
        // start, end, and teams are all present in the filter. When it changes, this needs
        // to be fetched as well
        KpiGoalActions.fetchUsedByDateAndTeams(startDate, endDate, teams.toJS());
      });
    }

    if (repDropped || filterChanged) {
      setTimeout(() => {
        SkillActions.fetchUsedByDate(startDate, endDate, teams.toJS());
      });
    }

    if (fetchHorizontal) {
      setTimeout(() =>
        MetricsTrendsActions.fetchMetric({
          metricAxis: MetricAxis.HORIZONTAL,
          direction: horizontal.metric.direction,
          metric: horizontal.metric.metric,
          context: horizontal.metric.context,
          ...metricFilter,
        })
      );
    }

    if (fetchVertical) {
      setTimeout(() =>
        MetricsTrendsActions.fetchMetric({
          metricAxis: MetricAxis.VERTICAL,
          direction: vertical.metric.direction,
          metric: vertical.metric.metric,
          context: vertical.metric.context,
          ...metricFilter,
        })
      );
    }
  }

  initialDataFetch() {
    const {startDate, endDate, managerId, selectedTeamId, location} = this.props;
    setTimeout(async () => {
      try {
        // Can be called immediately with default Props
        const skillPromise = this.props.teams.isEmpty()
          ? Promise.resolve(List())
          : SkillActions.fetchUsedByDate(startDate, endDate, this.props.teams.toJS());

        const repPromise = this.repDataLoad(location, startDate, endDate);
        const [allTeams, managerTeams] = await Promise.all([
          TeamActions.fetchTeamsForUser(),
          DirectReportsActions.directReports(managerId),
        ]);
        const teams = getTeams(managerId, selectedTeamId, allTeams, managerTeams);
        const metricFilter = getMetricFilter(startDate, endDate, teams);

        // Callable with previous await's result, but we don't need their result
        // here, so there's no need to await. But we can still start the call off and
        // get it processing. These promises will resolve at the end.
        const extraPromises = Promise.all([
          KpiGoalActions.fetchUsedByDateAndTeams(startDate, endDate, teams),
          metricFilter && MetricsActions.fetchTimeInformation(metricFilter),
          skillPromise,
          repPromise,
        ]);

        // These two calls rely on the metric filter generated from the teams response
        if (metricFilter) {
          const [hResponse, vResponse] = await Promise.all([
            MetricsTrendsActions.fetchMetric({
              metricAxis: MetricAxis.HORIZONTAL,
              metric: DEFAULT_HORIZONTAL_METRIC,
              ...metricFilter,
            }),
            MetricsTrendsActions.fetchMetric({
              metricAxis: MetricAxis.VERTICAL,
              metric: DEFAULT_VERTICAL_METRIC,
              ...metricFilter,
            }),
          ]);
          const repIds = uniq([
            ...Object.keys(hResponse?.data ?? {}),
            ...Object.keys(vResponse?.data ?? {}),
          ]);

          // These two calls rely on the repIds which we derive from the previous calls
          await Promise.all([
            DirectReportsActions.getSkillBreakdown(repIds),
            fetchMissingUsers(repIds),
          ]);
        }

        // Finally, ensure the rest of our calls finished resolving
        await extraPromises;
      } catch (e) {
        console.error(e);
      } finally {
        this.setState({initialLoad: false});
      }
    }, 0);
  }

  /**
   * Remove plans which:
   *   || are adHoc
   *   || are liveCoaching
   *   || Have no change in skill
   *   || have 0 time spent on coaching
   */
  filterRepPlans(repPlans: List<CoachingPlan> | undefined = List()) {
    const {velocityMap, timeSpentByPlan} = this.props;
    return repPlans.filter(plan => {
      const change = velocityMap.get(plan.id)?.changeInScore ?? 0;
      const timeSpent = timeSpentByPlan.get(plan.id, 0);

      if (plan.adHoc) return false;
      if (plan.livecoaching) return false;
      if (change === 0) return false;
      if (timeSpent === 0) return false;
      return true;
    });
  }

  render() {
    const {metricData, plansByRep, chartData} = this.props;
    const {initialLoad, repDataLoading} = this.state;
    const {loading, horizontal, vertical, time} = metricData;
    const selectedRep = this.getSelectedRep() ?? undefined;

    return (
      <NineBox
        {...this.props}
        metricsIsLoading={loading.metricHorizontal || loading.metricVertical || initialLoad}
        setAxisMetric={MetricsTrendsActions.setAxisMetric}
        horizontal={horizontal}
        vertical={vertical}
        onSelectRep={selectedRep => queryParams.set({selectedRep})}
        selectedDataPoint={chartData.find(d => d.rep.id === selectedRep)}
        meetingTime={time.meeting}
        reviewTime={time.review}
        repPlans={this.filterRepPlans(plansByRep.get(selectedRep))}
        repDataLoading={repDataLoading}
      />
    );
  }
}
